'use client';

import { useEffect, useState } from 'react';

export type DashboardTheme = 'dark' | 'light';

const STORAGE_KEY = 'fleetava-dashboard-theme';
const THEME_EVENT = 'fleetava-theme';

function isTheme(value: string | null): value is DashboardTheme {
  return value === 'light' || value === 'dark';
}

export function useDashboardTheme(): { theme: DashboardTheme; toggleTheme: () => void } {
  const [theme, setTheme] = useState<DashboardTheme>('light');

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) setTheme(stored);

    function onTheme(event: Event) {
      const next = (event as CustomEvent<string>).detail;
      if (isTheme(next)) setTheme(next);
    }
    window.addEventListener(THEME_EVENT, onTheme);
    return () => window.removeEventListener(THEME_EVENT, onTheme);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  function toggleTheme() {
    setTheme((current) => {
      const next: DashboardTheme = current === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(STORAGE_KEY, next);
      window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: next }));
      return next;
    });
  }

  return { theme, toggleTheme };
}
